import { useEffect, useState } from "react";
import { CheckCircle2, Download, Eye, Mail, MailCheck, Pencil, Plus, Send, Trash2, Undo2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur, INVOICE_STATUS, PAYMENT_METHODS } from "./docUtils";
import PaymentModal from "./PaymentModal";
import CreditNoteModal from "./CreditNoteModal";
import SendDocModal from "./SendDocModal";

const methodLabel = (m) => (PAYMENT_METHODS.find(([v]) => v === m) || [m, m])[1];

export default function InvoiceDetail({ invoiceId, onClose, onChanged, onEdit }) {
  const [inv, setInv] = useState(null);
  const [confirmValidate, setConfirmValidate] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [cnOpen, setCnOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [optPresent, setOptPresent] = useState(true);
  const [optSend, setOptSend] = useState(false);

  const load = () => invoiceId && api.get(`/accounting/invoices/${invoiceId}`).then((r) => setInv(r.data)).catch(() => {});
  useEffect(() => { load(); }, [invoiceId]);

  const refresh = () => { load(); onChanged(); };

  const validate = async () => {
    try {
      const r = await api.post(`/accounting/invoices/${inv.id}/validate`,
        { present_client: optPresent, send_email: optSend });
      toast.success(optSend
        ? `Facture N° ${r.data.number} validée et envoyée par email au client`
        : `Facture définitive — N° ${r.data.number}`);
      setConfirmValidate(false);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const markSent = async () => {
    if (!window.confirm("Confirmez que cette facture a été transmise au client hors application (email, courrier…) ? Les relances automatiques s'appliqueront.")) return;
    try {
      await api.post(`/accounting/invoices/${inv.id}/mark-sent`);
      toast.success("Facture marquée comme envoyée — le suivi et les relances continuent");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleVisible = async (visible) => {
    try {
      await api.post(`/accounting/invoices/${inv.id}/visibility`, { visible });
      toast.success(visible ? "Facture visible dans l'espace client" : "Facture masquée de l'espace client");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async () => {
    try {
      await api.delete(`/accounting/invoices/${inv.id}`);
      toast.success("Brouillon supprimé");
      onChanged();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openPdf = async () => {
    const r = await api.get(`/accounting/invoices/${inv.id}/pdf`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
  };

  const st = inv ? INVOICE_STATUS[inv.status] : null;
  const isDraft = inv?.status === "brouillon";
  const isFinal = inv && !isDraft;
  const reste = inv ? Math.max(0, inv.total_ttc - (inv.paid_total || 0)) : 0;

  return (
    <Dialog open={!!invoiceId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail" aria-describedby={undefined}>
        {!inv ? <div className="p-8 text-center text-sm text-neutral-400">Chargement…</div> : (
          <>
            <DialogTitle className="font-heading font-bold text-lg flex items-center gap-2 flex-wrap">
              {inv.number ? `Facture N° ${inv.number}` : "Facture — brouillon"}
              <span className={`text-[11px] rounded-full px-2.5 py-1 border ${st.cls}`} data-testid="invoice-detail-status">{st.label}</span>
            </DialogTitle>
            {inv.title && <p className="text-sm text-neutral-500 font-medium" data-testid="invoice-detail-title">{inv.title}</p>}
            {inv.quote_number && (
              <p className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5" data-testid="invoice-quote-ref">
                Facture établie à partir du devis n°{inv.quote_number} — Devis d'origine
              </p>
            )}

            <div className="space-y-5 mt-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="invoice-dates">
                <div><p className="text-[11px] uppercase tracking-wider text-neutral-400">Client</p><p className="font-medium">{inv.client_name}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-neutral-400">Émission</p><p className="font-medium">{inv.issue_date || "—"}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-neutral-400">Envoi</p><p className="font-medium">{(inv.sent_at || "").slice(0, 10) || "—"}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-neutral-400">Paiement</p><p className="font-medium">{inv.paid_at || "—"}</p></div>
              </div>

              <div className="border border-neutral-200 rounded-xl overflow-hidden" data-testid="invoice-items">
                {inv.items.map((it, i) => (
                  <div key={i} className="flex justify-between gap-3 px-4 py-2.5 text-sm border-b border-neutral-100 last:border-0">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{it.prestation}</p>
                      {it.description && <p className="text-xs text-neutral-400 whitespace-pre-line">{it.description}</p>}
                    </div>
                    <p className="text-neutral-600 shrink-0">{it.qty} × {eur(it.price_ht)}</p>
                  </div>
                ))}
                <div className="bg-neutral-50 px-4 py-3 text-sm space-y-1">
                  {(inv.discount_percent || 0) > 0 && (
                    <>
                      <div className="flex justify-between"><span className="text-neutral-500">Total HT (avant remise)</span><span>{eur(inv.total_ht_brut ?? inv.total_ht)}</span></div>
                      <div className="flex justify-between"><span className="text-neutral-500">Remise ({inv.discount_percent} %)</span><span className="text-red-600">− {eur(inv.discount_ht || 0)}</span></div>
                    </>
                  )}
                  <div className="flex justify-between"><span className="text-neutral-500">Total HT</span><span>{eur(inv.total_ht)}</span></div>
                  {inv.tva_mode !== "non_assujetti" && <div className="flex justify-between"><span className="text-neutral-500">TVA</span><span>{eur(inv.total_tva)}</span></div>}
                  <div className="flex justify-between font-bold text-base"><span>Total TTC</span><span data-testid="invoice-detail-total">{eur(inv.total_ttc)}</span></div>
                </div>
              </div>

              {isFinal && (
                <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 space-y-3" data-testid="invoice-payments">
                  <div className="flex justify-between text-sm">
                    <span>Payé : <strong className="text-green-700">{eur(inv.paid_total || 0)}</strong></span>
                    <span>Reste à payer : <strong className={reste > 0 ? "text-orange-600" : "text-green-700"} data-testid="invoice-reste">{eur(reste)}</strong></span>
                  </div>
                  <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${Math.min(100, ((inv.paid_total || 0) / inv.total_ttc) * 100)}%` }} />
                  </div>
                  {(inv.payments || []).map((p) => (
                    <div key={p.id} className="flex justify-between text-xs text-neutral-500" data-testid={`payment-row-${p.id}`}>
                      <span>{p.date} · {methodLabel(p.method)}</span><span className="font-medium text-green-700">{eur(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              {(inv.credit_notes || []).length > 0 && (
                <div className="space-y-1.5" data-testid="invoice-credit-notes">
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400">Avoirs liés</p>
                  {inv.credit_notes.map((cn) => (
                    <div key={cn.id} className="flex justify-between text-sm bg-red-50/50 border border-red-100 rounded-lg px-3 py-2">
                      <span>Avoir {cn.number}{cn.reason ? ` — ${cn.reason}` : ""}</span>
                      <span className="font-medium text-red-700">-{eur(cn.amount_ttc)}</span>
                    </div>
                  ))}
                </div>
              )}

              {(inv.email_log || []).length > 0 && (
                <div className="space-y-1.5" data-testid="invoice-email-log">
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400">Journal des emails</p>
                  {inv.email_log.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-neutral-500" data-testid={`email-log-${i}`}>
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      <span className="flex-1 truncate">{e.sent_at?.slice(0, 16).replace("T", " ")} · {e.kind || "envoi"} → {e.to} · {e.subject}</span>
                      <span className={e.status === "sent" ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                        {e.status === "sent" ? "Envoyé" : "Erreur"}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {confirmValidate && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3" data-testid="invoice-validate-confirm">
                  <p className="text-sm text-orange-900 font-medium">
                    Cette action va rendre la facture définitive et lui attribuer son numéro de facture. Continuer ?
                  </p>
                  <p className="text-xs text-orange-700/80">
                    Astuce : cliquez sur « Aperçu PDF » pour vérifier le document final (titre, adresses, lignes, totaux) avant de confirmer.
                  </p>
                  <label className="flex items-center gap-2 text-sm text-orange-900 cursor-pointer" data-testid="invoice-opt-present">
                    <input type="checkbox" className="rounded" checked={optPresent} onChange={(e) => setOptPresent(e.target.checked)} />
                    Présenter dans l'espace client
                  </label>
                  <label className="flex items-center gap-2 text-sm text-orange-900 cursor-pointer" data-testid="invoice-opt-send">
                    <input type="checkbox" className="rounded" checked={optSend} onChange={(e) => setOptSend(e.target.checked)} />
                    Envoyer une notification mail au client (PDF de la facture joint)
                  </label>
                  <div className="flex gap-2">
                    <Button onClick={validate} className="bg-orange-600 hover:bg-orange-700 text-white flex-1" data-testid="invoice-validate-confirm-btn">Oui, valider</Button>
                    <Button variant="outline" onClick={() => setConfirmValidate(false)} data-testid="invoice-validate-cancel">Annuler</Button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {isDraft && (
                  <>
                    <Button variant="outline" onClick={openPdf} className="gap-1.5" data-testid="invoice-preview-btn">
                      <Eye className="w-4 h-4" /> Aperçu PDF
                    </Button>
                    <Button onClick={() => setConfirmValidate(true)} className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5" data-testid="invoice-validate-btn">
                      <CheckCircle2 className="w-4 h-4" /> Valider la facture
                    </Button>
                    <Button variant="outline" onClick={() => onEdit(inv)} className="gap-1.5" data-testid="invoice-edit-btn">
                      <Pencil className="w-4 h-4" /> Modifier
                    </Button>
                    <Button variant="outline" onClick={remove} className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50" data-testid="invoice-delete-btn">
                      <Trash2 className="w-4 h-4" /> Supprimer
                    </Button>
                  </>
                )}
                {isFinal && (
                  <>
                    <Button onClick={() => setSendOpen(true)} className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="invoice-send-btn">
                      <Send className="w-4 h-4" /> Aperçu & envoi
                    </Button>
                    {inv.status === "validee" && (
                      <Button variant="outline" onClick={markSent} className="gap-1.5" data-testid="invoice-mark-sent-btn">
                        <MailCheck className="w-4 h-4" /> Marquer comme envoyée (hors application)
                      </Button>
                    )}
                    {inv.status !== "payee" && (
                      <Button onClick={() => setPayOpen(true)} className="bg-green-600 hover:bg-green-700 text-white gap-1.5" data-testid="invoice-add-payment">
                        <Plus className="w-4 h-4" /> Enregistrer un paiement
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => setCnOpen(true)} className="gap-1.5" data-testid="invoice-credit-note-btn">
                      <Undo2 className="w-4 h-4" /> Créer un avoir
                    </Button>
                    <Button variant="outline" onClick={openPdf} className="gap-1.5" data-testid="invoice-pdf-btn">
                      <Download className="w-4 h-4" /> PDF
                    </Button>
                  </>
                )}
              </div>

              {isFinal && (
                <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer" data-testid="invoice-visible-toggle">
                  <input type="checkbox" className="rounded" checked={inv.visible_client !== false}
                    onChange={(e) => toggleVisible(e.target.checked)} />
                  Visible dans l'espace client
                  {inv.visible_client === false && <span className="text-[11px] text-neutral-400">(actuellement masquée au client)</span>}
                </label>
              )}
            </div>

            <PaymentModal invoice={payOpen ? inv : null} onClose={() => setPayOpen(false)} onSaved={refresh} />
            <CreditNoteModal invoice={cnOpen ? inv : null} onClose={() => setCnOpen(false)} onSaved={refresh} />
            <SendDocModal doc={sendOpen ? inv : null} kind="invoice" onClose={() => setSendOpen(false)} onSent={refresh} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
